import { lazy, type ComponentType } from 'react';

/**
 * `lazy()` wrapper that recovers from post-deploy stale-chunk errors.
 *
 * The problem: after a deploy, Vite emits bundles with new content-
 * hashed filenames (e.g. `ServerSettingsModal-XYZ.js`). A user who
 * loaded the page BEFORE the deploy still has the OLD `index.js`
 * in memory; when they click anything that triggers a lazy import
 * the SPA fetches the OLD filename, which 404s on the server. The
 * promise rejects with `Failed to fetch dynamically imported module`,
 * Suspense bubbles the rejection, and without an Error Boundary the
 * whole tree white-screens.
 *
 * Solution: catch the chunk-load error and reload the page. The
 * reload pulls fresh `index.html` (nginx has `no-cache` on it) which
 * references the new chunk filenames — the user's next click hits
 * the fresh bundle and works.
 *
 * `sessionStorage` guards against an infinite reload loop: if a
 * reload already happened in THIS tab and the import STILL fails,
 * the chunk is genuinely missing on the server and something is
 * broken — let the original error propagate so a real Error Boundary
 * (or the user) can surface it.
 *
 * Usage:
 *
 *     const ServerSettings = lazyWithReload(() =>
 *       import('./ServerSettingsModal').then((m) => ({ default: m.X }))
 *     );
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /Loading chunk \d+ failed/i.test(msg);
      const KEY = 'bunker:chunk-reload-attempted';
      let alreadyAttempted = false;
      try { alreadyAttempted = !!sessionStorage.getItem(KEY); } catch { /* restricted context */ }
      if (isChunkError && typeof window !== 'undefined' && !alreadyAttempted) {
        try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* restricted context */ }
        window.location.reload();
        // Suspense keeps the fallback up while we reload. Never resolve.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
